import React, { useState, useMemo } from 'react';
import { tWithKeyAttr as t } from '../../../i18nHelper';
import Modal from '../modal/Modal';
import { IPopupWithoutProps } from '../../../types/types';
import { IUserReport } from '../../../services/types/users.types';
import { useUpdateUserBalanceMutation } from '../../../services/api/user.api';
import styles from '../userDebtModal/userDebtModal.module.css';
import GeneralSuccessPopup from '../generalSuccessPopup/generalSuccessPopup';
import ErrorPopup from '../errorPopup/errorPopup';

interface UpdateUserBalancePopupProps extends IPopupWithoutProps {
  user: IUserReport;
  onSuccess?: () => void;
}

function UpdateUserBalancePopup({
  onClose,
  user,
  onSuccess,
}: UpdateUserBalancePopupProps) {
  const [balance, setBalance] = useState<string>(String(user.Balance ?? 0));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const [updateUserBalance, { isLoading }] = useUpdateUserBalanceMutation();

  const handleSubmit = async () => {
    const userGuid = user.Users?.Guid;
    if (!reason.trim() || !userGuid) return;

    try {
      await updateUserBalance({
        userGuid,
        newBalance: parseFloat(balance),
        updateReason: reason.trim(),
      }).unwrap();
      setSuccess(true);
      onSuccess?.();
    } catch (error: any) {
      setError(error?.data?.message ?? t('GENERIC_ERROR'));
    }
  };

  const canSubmit = reason.trim().length > 0 && !!user.Users?.Guid;

  const currentBalance = user.Balance ?? 0;
  const newBalanceParsed = parseFloat(balance);
  const delta = isNaN(newBalanceParsed) ? null : newBalanceParsed - currentBalance;

  const changeSummary = useMemo((): { isPositive: boolean; effectLine: string; totalChange: string } | null => {
    if (delta === null || delta === 0) return null;

    const absDelta = Math.abs(delta);
    const parts: string[] = [];
    if (currentBalance < 0 && newBalanceParsed >= 0) {
      parts.push(t('BALANCE_CHANGE_REMOVE_DEBT', { amount: Math.abs(currentBalance) }));
      if (newBalanceParsed > 0) parts.push(t('BALANCE_CHANGE_GRANT_CREDIT', { amount: newBalanceParsed }));
    } else if (currentBalance > 0 && newBalanceParsed <= 0) {
      parts.push(t('BALANCE_CHANGE_REMOVE_CREDIT', { amount: currentBalance }));
      if (newBalanceParsed < 0) parts.push(t('BALANCE_CHANGE_ADD_DEBT', { amount: Math.abs(newBalanceParsed) }));
    } else if (delta > 0) {
      parts.push(currentBalance < 0
        ? t('BALANCE_CHANGE_REDUCE_DEBT', { amount: absDelta })
        : t('BALANCE_CHANGE_GRANT_CREDIT', { amount: absDelta }));
    } else {
      parts.push(currentBalance > 0
        ? t('BALANCE_CHANGE_REDUCE_CREDIT', { amount: absDelta })
        : t('BALANCE_CHANGE_ADD_DEBT', { amount: absDelta }));
    }
    const effectLine = parts.join(` ${t('BALANCE_CHANGE_AND')} `);
    const sign = delta > 0 ? '+' : '';
    const totalChange = t('BALANCE_CHANGE_TOTAL', { change: `‎${sign}${delta}` });

    return { isPositive: delta > 0, effectLine, totalChange };
  }, [delta, currentBalance, newBalanceParsed]);
  if (success) {
    return (
      <Modal onClose={onClose}>
        <GeneralSuccessPopup onClose={onClose} />
      </Modal>
    );
  }
  if (error) {
    return (
      <Modal onClose={()=>{setError(null)}}>
        <ErrorPopup onClose={()=>{setError(null)}} text={error} />
      </Modal>
    );
  }
  return (
    <Modal onClose={onClose} wide>
      <article className={styles.container}>
        <h2 className={styles.title}>{t('UPDATE_USER_BALANCE')}</h2>

        <div className={styles.warningMessage}>
          <span className={styles.warningIcon}>ℹ️</span>
          <span className={styles.warningText}>{t('MANUAL_UPDATE_USER_BALANCE_NOTE')}</span>
        </div>

        <div className={styles.chargeAmountSection}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label className={styles.chargeAmountLabel}>{t('UPDATE_BALANCE')}:</label>
              <span style={{
                fontSize: 13,
                color: '#64748b',
                backgroundColor: '#f1f5f9',
                borderRadius: 4,
                padding: '2px 8px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}>
                {t('CURRENT_BALANCE')}: <strong style={{ color: currentBalance < 0 ? '#dc2626' : '#16a34a', display: 'inline-block', direction: 'ltr', unicodeBidi: 'embed' }}>‎{currentBalance}</strong>
              </span>
            </div>
            <input
              type="text"
              className={styles.chargeAmountInput}
              value={balance}
              onChange={(e) => {
                const val = e.target.value;
                // allow: digits, single leading minus, single decimal point
                if (/^-?\d*\.?\d*$/.test(val)) {
                  setBalance(val);
                }
              }}
              step="0.01"
              placeholder="0.00"
            />
          </div>
          {changeSummary && (() => {
            const color = changeSummary.isPositive ? '#16a34a' : '#dc2626';
            const bg = changeSummary.isPositive ? '#f0fdf4' : '#fef2f2';
            return (
              <div style={{
                marginTop: 12,
                padding: '12px 16px',
                borderRadius: 6,
                borderLeft: `4px solid ${color}`,
                backgroundColor: bg,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  direction: 'ltr',
                  fontSize: 20,
                  fontWeight: 700,
                  color,
                  lineHeight: 1,
                }}>
                  <span>‎{currentBalance}</span>
                  <span style={{ fontSize: 16, opacity: 0.7 }}>→</span>
                  <span>‎{newBalanceParsed}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color }}>
                  ⚠️ {changeSummary.effectLine}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#334155' }}>
                  {changeSummary.totalChange}
                </div>
              </div>
            );
          })()}
        </div>

        <div className={styles.commentWrapper} style={{ flexDirection: 'column', alignItems: 'stretch', marginBottom: 16 }}>
          <label className={styles.commentLabel}>{t('REASON')}:</label>
          <textarea
            className={styles.commentInput}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('REASON')}
            rows={3}
            maxLength={500}
          />
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={isLoading}
          >
            {t('CANCEL')}
          </button>
          <button
            type="button"
            className={styles.payBtn}
            onClick={handleSubmit}
            disabled={isLoading || !canSubmit}
          >
            {isLoading ? t('PROCESSING') : t('SAVE')}
          </button>
        </div>
      </article>
    </Modal>
  );
}

export default UpdateUserBalancePopup;
